import type { ColumnDef } from "@tanstack/react-table";
import { AlertCircle, AlertTriangle, LoaderCircle, Pencil, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { canManageFactory } from "@/app/guards";
import { useObraId } from "@/app/useObraId";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ChipsRelacion } from "@/components/ui/ChipsRelacion";
import { DataTable } from "@/components/ui/data-table";
import { ExportButton } from "@/components/ui/ExportButton";
import { type CampoFiltro, FiltrosBar } from "@/components/ui/filtros-bar";
import { ImportButton } from "@/components/ui/ImportButton";
import { Modal } from "@/components/ui/modal";
import { ApiError } from "@/lib/api";
import { useMe } from "@/lib/auth";
import {
  type Candado,
  type EstadoCandado,
  useCandados,
  useCreateCandado,
  useDeleteCandado,
  useUpdateCandado,
} from "@/lib/candados";
import { AVISO_FILTRADO_LOCAL, mensajeErrorLista, useFiltrosURL } from "@/lib/filtros";
import { CLAVES_CANDADOS } from "@/lib/filtros/candados";
import { CandadoForm, type CandadoFormValues } from "./CandadoForm";

// Etiqueta legible de cada estado operativo: rotula las opciones del filtro de estado en la
// FiltrosBar (del SERVIDOR, #247) y es el accessor de la columna, del que tira la ordenación.
// El badge no la usa: EstadoBadge escribe su propio texto.
const ESTADO_CANDADO_LABEL: Record<EstadoCandado, string> = {
  LIBRE: "Libre",
  EN_USO: "En uso",
};

// toInput normaliza los valores del formulario al payload del API (descripción vacía
// ⇒ null).
function toInput(values: CandadoFormValues) {
  const descripcion = values.descripcion.trim();
  return {
    codigo_candado: values.codigo_candado.trim(),
    descripcion: descripcion === "" ? null : descripcion,
    activo: values.activo,
  };
}

/**
 * EstadoBadge muestra el estado operativo en tiempo real del candado, que el servidor
 * deriva de los bloqueos vivos (#243): "EN_USO" mientras el candado cuelga de un bloqueo
 * activo, y enciende la ALERTA roja.
 */
function EstadoBadge({ estado }: { estado: EstadoCandado }) {
  if (estado === "EN_USO") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 font-semibold text-red-700 text-xs">
        <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
        ALERTA: EN USO
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 font-medium text-emerald-700 text-xs">
      Libre
    </span>
  );
}

/**
 * CandadosPage es el inventario de candados físicos con su estado en tiempo real
 * (libre / en uso) y el CRUD completo (alta/edición en modal, borrado con
 * confirmación). La tabla usa el DataTable de react-table (#34) SIN buscador en memoria
 * (`sinBuscador`): el texto libre y el filtro por estado son del SERVIDOR y viven en la
 * FiltrosBar (#247), y la tabla solo ordena y pagina, así que sus `accessorFn` alimentan la
 * celda, la ordenación o ambas, nunca una búsqueda (#346). Además exporta e importa xlsx.
 */
export function CandadosPage() {
  const { data: me } = useMe();
  const obraId = useObraId();
  const puedeGestionar = me != null && canManageFactory(me, obraId);

  // Los filtros viven en la URL (#247): compartible, recargable y con el servidor filtrando.
  const { filtros, setFiltros } = useFiltrosURL(CLAVES_CANDADOS);
  const {
    data: candados,
    isPending,
    isError,
    baseVacia,
    avisoSinConexion,
    codigoError,
    camposError,
  } = useCandados(obraId, filtros);
  const filas = candados ?? [];
  const crear = useCreateCandado(obraId);
  const editar = useUpdateCandado(obraId);
  const borrar = useDeleteCandado(obraId);

  // Estado de los diálogos: alta ("nuevo"), edición (candado) o borrado (candado).
  const [formState, setFormState] = useState<
    { mode: "nuevo" } | { mode: "editar"; candado: Candado } | null
  >(null);
  const [aBorrar, setABorrar] = useState<Candado | null>(null);

  const columns = useMemo<ColumnDef<Candado, unknown>[]>(() => {
    const cols: ColumnDef<Candado, unknown>[] = [
      {
        accessorKey: "codigo_candado",
        header: "Código",
        cell: ({ row }) => (
          <div className="flex items-center gap-2 font-medium text-slate-900">
            <span>{row.original.codigo_candado}</span>
            {row.original.activo ? null : (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-500 text-xs">
                Inactivo
              </span>
            )}
          </div>
        ),
      },
      {
        id: "descripcion",
        accessorFn: (c) => c.descripcion ?? "—",
        header: "Descripción",
        cell: ({ getValue }) => <span className="text-slate-500">{getValue<string>()}</span>,
        meta: { className: "hidden sm:table-cell" },
      },
      {
        id: "estado",
        accessorFn: (c) => ESTADO_CANDADO_LABEL[c.estado],
        header: "Estado",
        cell: ({ row }) => <EstadoBadge estado={row.original.estado} />,
      },
      // Relación cruzada (#248): dónde está puesto el candado ahora mismo. El accessorFn
      // devuelve los NOMBRES de los puntos concatenados —la celda pinta sus chips por su
      // cuenta— y su único cliente es la ordenación de la columna (#346).
      {
        id: "puntos",
        accessorFn: (c) => c.puntos.map((p) => p.nombre).join(" "),
        header: "Ubicación",
        meta: { stopRowClick: true },
        cell: ({ row }) => {
          const puntos = row.original.puntos;
          const equipos = puntos.filter(
            (punto, indice) =>
              puntos.findIndex((otro) => otro.id_equipo === punto.id_equipo) === indice,
          );
          if (puntos.length === 0) {
            return <span className="text-slate-500">Sin puntos</span>;
          }
          return (
            <div className="flex min-w-44 flex-col gap-2">
              <div className="flex items-start gap-1.5">
                <span className="min-w-14 pt-0.5 text-slate-500 text-xs">Puntos</span>
                <ChipsRelacion
                  vacio="Sin puntos"
                  visibles={puntos.length}
                  items={puntos.map((punto) => ({
                    clave: punto.id_punto,
                    etiqueta: punto.nombre,
                    titulo: `Ver el punto ${punto.nombre}`,
                    enlace: {
                      to: "/obras/$obraId/puntos",
                      params: { obraId: String(obraId) },
                      search: { equipo: String(punto.id_equipo), q: punto.nombre },
                    },
                  }))}
                />
              </div>
              <div className="flex items-start gap-1.5">
                <span className="min-w-14 pt-0.5 text-slate-500 text-xs">Equipos</span>
                <ChipsRelacion
                  vacio="Sin equipos"
                  visibles={equipos.length}
                  items={equipos.map((equipo) => ({
                    clave: equipo.id_equipo,
                    etiqueta: equipo.codigo_equipo,
                    titulo: `Ver el equipo ${equipo.codigo_equipo}`,
                    enlace: {
                      to: "/obras/$obraId/equipos",
                      params: { obraId: String(obraId) },
                      search: { q: equipo.codigo_equipo },
                    },
                  }))}
                />
              </div>
            </div>
          );
        },
      },
      // El LOTO del bloqueo activo, enlazado a su ficha; vacío si el candado está libre.
      // La celda para el clic de la fila para que el enlace navegue en vez de abrir el modal.
      {
        id: "loto",
        accessorFn: (c) => c.loto?.numero_loto ?? "",
        header: "LOTO",
        meta: { className: "hidden md:table-cell", stopRowClick: true },
        cell: ({ row }) => (
          <ChipsRelacion
            vacio="—"
            items={
              row.original.loto
                ? [
                    {
                      clave: row.original.loto.id_loto,
                      etiqueta: row.original.loto.numero_loto,
                      enlace: {
                        to: "/obras/$obraId/lotos/$id",
                        params: {
                          obraId: String(obraId),
                          id: String(row.original.loto.id_loto),
                        },
                      },
                    },
                  ]
                : []
            }
          />
        ),
      },
      {
        accessorKey: "num_usos",
        header: "Usos",
        meta: { className: "text-right" },
        cell: ({ row }) => <span className="tabular-nums">{row.original.num_usos}</span>,
      },
    ];
    if (puedeGestionar) {
      cols.push({
        id: "acciones",
        header: () => <span className="sr-only">Acciones</span>,
        enableSorting: false,
        enableGlobalFilter: false,
        meta: { className: "text-right", stopRowClick: true },
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Editar ${row.original.codigo_candado}`}
              onClick={() => setFormState({ mode: "editar", candado: row.original })}
            >
              <Pencil className="h-5 w-5" aria-hidden="true" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Eliminar ${row.original.codigo_candado}`}
              onClick={() => setABorrar(row.original)}
            >
              <Trash2 className="h-5 w-5 text-red-600" aria-hidden="true" />
            </Button>
          </div>
        ),
      });
    }
    return cols;
  }, [obraId, puedeGestionar]);

  // Campos de la barra de filtros. Los valores son los del API (el enum), no las etiquetas.
  const campos = useMemo<CampoFiltro[]>(
    () => [
      { tipo: "texto", nombre: "q", etiqueta: "Buscar", placeholder: "Buscar candados…" },
      {
        tipo: "select",
        nombre: "estado",
        etiqueta: "Estado",
        opciones: (["LIBRE", "EN_USO"] as const).map((e) => ({
          valor: e,
          etiqueta: ESTADO_CANDADO_LABEL[e],
        })),
      },
      { tipo: "booleano", nombre: "activo", etiqueta: "Activo" },
    ],
    [],
  );

  function cerrarForm() {
    setFormState(null);
  }

  async function onSubmitForm(values: CandadoFormValues) {
    if (formState?.mode === "editar") {
      await editar.mutateAsync({ id: formState.candado.id, input: toInput(values) });
    } else {
      await crear.mutateAsync(toInput(values));
    }
    cerrarForm();
  }

  async function confirmarBorrado() {
    if (!aBorrar) {
      return;
    }
    try {
      await borrar.mutateAsync(aBorrar.id);
      setABorrar(null);
    } catch {
      // El fallo (p. ej. 409 CANDADO_EN_USO) se refleja en borrar.error y se muestra en el
      // modal, que se mantiene abierto para que el usuario lo lea.
    }
  }

  // Mensaje del error de borrado: el candado puesto (#243) se explica aparte del genérico.
  const mensajeBorrado =
    borrar.error instanceof ApiError && borrar.error.code === "CANDADO_EN_USO"
      ? "El candado está colocado en un LOTO. Retíralo antes de eliminarlo."
      : "No se pudo eliminar el candado. Inténtalo de nuevo.";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col">
          <h1 className="font-bold text-2xl text-slate-900 tracking-tight">Candados</h1>
          <p className="text-base text-slate-500">
            Inventario de candados físicos y su estado en tiempo real.
          </p>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <ImportButton tipo="candados" etiqueta="candados" obraId={obraId} />
          <ExportButton tipo="candados" obraId={obraId} filtros={filtros} />
          {puedeGestionar ? (
            <Button onClick={() => setFormState({ mode: "nuevo" })}>
              <Plus className="h-5 w-5" aria-hidden="true" />
              Nuevo candado
            </Button>
          ) : null}
        </div>
      </div>

      {isError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>
            {mensajeErrorLista(
              { codigoError, camposError },
              "No se pudieron cargar los candados. Inténtalo de nuevo.",
            )}
          </span>
        </Alert>
      ) : null}

      {isPending ? (
        <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
          <LoaderCircle className="h-6 w-6 animate-spin" aria-hidden="true" />
          Cargando candados…
        </div>
      ) : null}

      {!isPending && !isError && baseVacia ? (
        <div className="rounded-2xl border border-slate-200 border-dashed bg-white px-4 py-12 text-center text-slate-500">
          Aún no hay candados.{puedeGestionar ? " Crea el primero con «Nuevo candado»." : ""}
        </div>
      ) : null}

      {!baseVacia ? (
        <>
          <FiltrosBar
            campos={campos}
            valor={filtros}
            onChange={setFiltros}
            aviso={avisoSinConexion ? AVISO_FILTRADO_LOCAL : undefined}
          />
          {!isPending ? (
            <DataTable
              columns={columns}
              data={filas}
              sinBuscador
              onRowClick={
                puedeGestionar ? (candado) => setFormState({ mode: "editar", candado }) : undefined
              }
            />
          ) : null}
        </>
      ) : null}

      {/* Alta / edición */}
      <Modal
        open={formState !== null}
        onClose={cerrarForm}
        title={formState?.mode === "editar" ? "Editar candado" : "Nuevo candado"}
      >
        {formState !== null ? (
          <CandadoForm
            defaultValues={
              formState.mode === "editar"
                ? {
                    codigo_candado: formState.candado.codigo_candado,
                    descripcion: formState.candado.descripcion ?? "",
                    activo: formState.candado.activo,
                  }
                : { codigo_candado: "", descripcion: "", activo: true }
            }
            submitLabel={formState.mode === "editar" ? "Guardar cambios" : "Crear candado"}
            pending={crear.isPending || editar.isPending}
            onSubmit={onSubmitForm}
            onCancel={cerrarForm}
          />
        ) : null}
      </Modal>

      {/* Confirmación de borrado */}
      <Modal open={aBorrar !== null} onClose={() => setABorrar(null)} title="Eliminar candado">
        <p className="text-base text-slate-600">
          ¿Seguro que quieres eliminar el candado <strong>{aBorrar?.codigo_candado}</strong>? Esta
          acción lo da de baja.
        </p>
        {borrar.isError ? (
          <Alert variant="destructive">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <span>{mensajeBorrado}</span>
          </Alert>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setABorrar(null)} disabled={borrar.isPending}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => void confirmarBorrado()}
            disabled={borrar.isPending}
          >
            {borrar.isPending ? (
              <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
            ) : (
              <Trash2 className="h-5 w-5" aria-hidden="true" />
            )}
            Eliminar
          </Button>
        </div>
      </Modal>
    </div>
  );
}
